import { Customer, IntervalConfig, IntervalKind, IntervalPhase, IntervalType, Quote, QuoteOrderLine } from '../types';
import type { TypeDigit } from './documentNumberService';
import type { AnlageTyp } from './projektAnlageRechte';
import { auftragswertCentsAusAngebot } from './erpSteering';
import { typeDigitFromDocumentNumber } from './quoteLogic';
import { isHourlyUnit } from './serviceUnits';

/** Werte, die der Bestaetigungsdialog vor der Projektanlage abfragt. */
export interface AcceptQuoteValues {
    /**
     * Hauptverantwortlicher (Ausfuehrung, Projektbesitzer, Position 0 der
     * Vertreterliste). Leer = noch nicht gewaehlt. Pflichtfeld im Dialog,
     * bewusst ohne Vorbelegung.
     */
    coordinatorId: string;
    /**
     * Verantwortliche Planungsphase (docs/projekt-kickoff-plan.md §5): profiles.id
     * in Auswahlreihenfolge. Leer oder fehlend = der Hauptverantwortliche plant
     * selbst. acceptQuote haengt die Personen mit `bereich: 'planung'` an die
     * Vertreterliste — der Kickoff-Trigger legt fuer jede eine Quittung an.
     */
    planningUserIds?: string[];
    /**
     * Projekttyp des neuen Projekts (`metadata.projectType`). Im Dialog ein
     * Pflichtfeld: das Angebot traegt keinen verlaesslichen Typ, und bis
     * 19.10.2026 entstand aus JEDER Annahme ein SiGeKo-Projekt — auch aus
     * einer SiFa-Betreuung. Fehlt = SiGeKo wie bisher (Aufrufe ohne Dialog).
     */
    projectType?: AnlageTyp;
    projectName: string;
    projectNumber: string;
    address: string;
    startDate: string;
    endDate: string;
    intervalConfig: IntervalConfig;
    /** Geocodierte Baustellenadresse, damit das Projekt im Umfeld-Check auftaucht. */
    coordinates?: { lat: number; lng: number };
    /**
     * Fixierter Auftragswert in CENT (ERP/CRM-Plan Abschnitt 8,
     * „Auftragswert-Feststellung"). Vorbelegt mit der Angebots-Nettosumme,
     * im Dialog editierbar. 0 = nicht fixieren — dann entsteht keine Zeile in
     * `project_order_values` und das Projekt taucht im Auftragsbestand unter
     * „ohne Auftragswert" auf, wo es nachgetragen werden kann.
     */
    orderValueCents: number;
    /**
     * Mindeststunden je STUNDEN-Angebotsposition (QuoteItem.id -> Stunden).
     * Steht bewusst nur hier und nicht am QuoteItem: das Angebot kennt keine
     * Mindeststunden, gefragt wird erst unmittelbar vor der Projektanlage.
     * Die Werte landen ueber quoteToReport auf den ServiceItems des Projekts.
     */
    minHoursByItemId?: Record<string, number>;
    /**
     * TEILBEAUFTRAGUNG: welche Positionen mit welcher Menge beauftragt sind.
     * Fehlt = vollständig (alle offenen Mengen) — das bisherige Verhalten.
     * Nur diese Mengen gehen ins Projekt-LV; der Rest bleibt am Angebot offen.
     */
    orderLines?: QuoteOrderLine[];
    /** Bestellnummer / Zeichen des Kunden (Auftragsteil, Projektnotiz). */
    customerRef?: string;
    /** Datum der Beauftragung durch den Kunden (yyyy-mm-dd). */
    orderDate?: string;
    /** Wer die Annahme erfasst (Auftragsteil: by/byName). */
    actor?: { id?: string; name: string };
}

/**
 * Stundenpositionen eines Angebots — die Positionen, fuer die der
 * Annahmedialog nach Mindeststunden fragt.
 *
 * BEWUSST OHNE Vorbelegung: eine automatisch gesetzte 1 waere eine Vorgabe,
 * die niemand vereinbart hat. Der Buchungsdialog zeigte dann dauerhaft
 * "Mindestens 1 Std. ansetzen." und warnte bei jeder halben Stunde. Vorschlagen
 * soll der Mensch, der das Projekt anlegt; bis dahin gilt "keine Vorgabe" und
 * die Buchung verhaelt sich exakt wie bisher (defaultHoursForService -> 1).
 */
export const hourlyQuoteItems = <T extends { unit?: string }>(items: T[]): T[] =>
    (items || []).filter((item) => isHourlyUnit(item.unit));

/**
 * Typziffer der Projektnummer (JJMM<T>NNN) je Projekttyp — dieselbe Regel wie
 * der Anlage-Assistent: 1 = SiGeKo, 2 = SiFa, sonst 3.
 */
export const typzifferFuerProjektTyp = (typ: AnlageTyp): TypeDigit =>
    typ === 'sifa' ? '2' : typ === 'hse' ? '3' : '1';

/**
 * Vorbelegung des Projekttyps aus der Angebotsnummer. Nur die Typziffer 2 ist
 * eindeutig (SiFa): 1 ist die Vorgabe beim Anlegen eines Angebots und sagt
 * nichts — A-26101010 war eine SiFa-Betreuung mit Ziffer 1 —, und 3 heisst
 * „Sonstige" (HSE oder etwas anderes). Dann bleibt die Wahl offen.
 */
export const projektTypAusAngebotsnummer = (quoteNumber?: string): AnlageTyp | undefined =>
    typeDigitFromDocumentNumber(quoteNumber) === '2' ? 'sifa' : undefined;

// Angebots-Intervalle (mehrphasig) auf die einphasige Projekt-IntervalConfig.
const INTERVAL_MAP: Record<IntervalKind, IntervalType> = {
    taeglich: 'daily',
    woechentlich: 'weekly',
    '14taeglich': 'biweekly',
    '2xmonat': 'twice_monthly',
    '1xmonat': 'monthly',
    individuell: 'custom',
};

const joinAddress = (street?: string, zip?: string, city?: string): string =>
    [street, [zip, city].filter(Boolean).join(' ')]
        .map((p) => (p || '').trim())
        .filter(Boolean)
        .join(', ');

/**
 * Das Projekt kennt nur EIN Intervall, das Angebot mehrere Phasen: die erste
 * Phase gibt die Art vor. Bewusst verlustbehaftet – der Dialog zeigt das
 * Ergebnis, damit es vor der Anlage korrigiert werden kann.
 */
const toIntervalConfig = (phases: IntervalPhase[]): IntervalConfig => {
    const first = phases[0];
    if (!first) return { type: 'manual' };
    const type = INTERVAL_MAP[first.interval] ?? 'manual';
    if (type !== 'custom') return { type };
    const perWeek = first.perWeek && first.perWeek > 0 ? first.perWeek : 1;
    return { type: 'custom', customDays: Math.max(1, Math.round(7 / perWeek)) };
};

/**
 * Vorbelegung des Bestaetigungsdialogs aus Angebot und Kunde. Rein und ohne
 * Supabase-Zugriff, damit die gesamte Ableitung testbar bleibt – die
 * Projektnummer zieht der Dialog serverseitig nach.
 */
export const quoteAcceptDefaults = (
    quote: Quote,
    customer: Customer,
    todayIso: string,
): AcceptQuoteValues => {
    const d = quote.details || {};
    const phases = d.intervalPhases || [];
    const froms = phases.map((p) => p.from).filter(Boolean).sort();
    const tos = phases.map((p) => p.to).filter(Boolean).sort();

    const siteAddress = joinAddress(d.siteStreet, d.siteZip, d.siteCity);

    return {
        // Bewusst LEER: eine Vorbelegung (frueher der angemeldete Nutzer) wurde
        // beim Durchklicken uebernommen und das Projekt landete beim Falschen.
        coordinatorId: '',
        // Leer: im Normalfall plant der Hauptverantwortliche selbst.
        planningUserIds: [],
        // Nur bei eindeutiger Angebotsnummer vorbelegt, sonst Pflichtwahl.
        projectType: projektTypAusAngebotsnummer(quote.quoteNumber),
        projectName: quote.title || `${customer.name} – ${quote.quoteNumber}`,
        projectNumber: '',
        address: siteAddress || customer.address || '',
        startDate: d.projectStart || froms[0] || todayIso.slice(0, 10),
        endDate: d.projectEnd || tos[tos.length - 1] || '',
        intervalConfig: toIntervalConfig(phases),
        // NETTO und nicht brutto: der Auftragsbestand rechnet gegen
        // fakturiertes Netto (supabase/erp_steering.sql), Umsatzsteuer ist kein
        // Auftragswert. Nur ein Vorschlag — der Dialog laesst ihn aendern.
        orderValueCents: auftragswertCentsAusAngebot(quote.netTotal),
        // Leer: die Mindeststunden traegt der Anlegende im Dialog ein.
        minHoursByItemId: {},
        // Vollstaendig ist die Vorgabe (orderLines leer); Datum = heute.
        orderDate: todayIso.slice(0, 10),
    };
};
